/// <amd-module name="WorkItemsHub/Components/WorkItemsHubView" />
import * as React from "react";
import * as Resources from "WorkItemsHub/Resources";

import { IObservableArray } from "VSS/Core/Observable";
import { ContributedPivotBarItemProvider } from 'VSS/Features/Frame/ContributedPivotBarItemProvider';
import { IVssPageContext } from 'VSS/Platform/Context';
import { IVssComponentContext, VssComponent } from 'VSS/Platform/Layout';
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Hub } from "VSSUI/Hub";
import { HubHeader } from "VSSUI/HubHeader";
import { IVssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import {
    IPivotBarAction,
    IPivotBarViewAction,
    IPivotRenderingModeOptions,
    PivotRenderingMode
} from "VSSUI/PivotBar";
import { HubViewOptionKeys } from "VSSUI/Utilities/HubViewState";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/Common/Constants/CustomerInteligence";
import { ActionsCreator } from "WorkItemsHub/Actions/ActionsCreator";
import { WorkItemsTabFilter } from "WorkItemsHub/Components/WorkItemsTabFilter";
import { WorkItemsHubTabGroupContributionId } from "WorkItemsHub/Constants";
import { WorkItemsHubStore } from "WorkItemsHub/Stores/WorkItemsHubStore";

export interface IWorkItemsHubViewProp {
    pageContext: IVssPageContext;
    store: WorkItemsHubStore;
    actionsCreator: ActionsCreator;
    hubViewState: IVssHubViewState;
    commands: IPivotBarAction[] | IObservableArray<IPivotBarAction>;
    viewActions: IPivotBarViewAction[] | IObservableArray<IPivotBarViewAction>;
}

export class WorkItemsHubView extends VssComponent<IWorkItemsHubViewProp, {}> {
    private _pivotProvider: ContributedPivotBarItemProvider;

    private _workItemsTabFilter: WorkItemsTabFilter;
    private _focusFilterBarOnMount: boolean;

    constructor(props: IWorkItemsHubViewProp, context: IVssComponentContext) {
        super(props, context);

        this._pivotProvider = new ContributedPivotBarItemProvider({
            pageContext: this.props.pageContext,
            tabGroupIds: [WorkItemsHubTabGroupContributionId],
        });
    }

    public componentDidMount(): void {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(
            WITCustomerIntelligenceArea,
            WITCustomerIntelligenceFeature.NEW_BOARDS_PLATFORM,
            {
                source: "WorkItems",
            }
        );
    }

    public render(): JSX.Element {
        const { hubViewState, viewActions, commands, store } = this.props;
       
        return (
            <Hub
                className="work-items-hub-view flex-grow"
                hubViewState={hubViewState}
                commands={commands}
                viewActions={viewActions}
                pivotProviders={[this._pivotProvider]}
                pivotRenderingModeOptions={this._getPivotRenderingModeOptions()}
                onRenderFilterBar={() => <WorkItemsTabFilter
                    filter = {hubViewState.filter}
                    tabId = {hubViewState.selectedPivot.value.toLowerCase()}
                    store = {store}
                    ref={this._resolveWorkItemsTabFilter}
                />}>
                <HubHeader title={Resources.WorkItemsHubTitle}/>
            </Hub >
        );
    }

    /**
     * Shows and focuses on the filter bar.
     */
    public activateFilter(): void {
        this.props.hubViewState.viewOptions.setViewOption(HubViewOptionKeys.showFilterBar, true);

        if (this._workItemsTabFilter) {
            this._workItemsTabFilter.focusFilterBar();
        }
        else {
            this._focusFilterBarOnMount = true; // remember to focus on render if the filter bar was previously not mounted
        }
    }

    private _getPivotRenderingModeOptions(): IPivotRenderingModeOptions {
        return {
            mode: PivotRenderingMode.DropDown,
            props: {
                dropdownWidth: 180
            }
        } as IPivotRenderingModeOptions;
    }
    
    public toggleFullScreen(): void {
        const isFullScreen = this.props.hubViewState.viewOptions.getViewOption(HubViewOptionKeys.fullScreen);
        this.props.hubViewState.viewOptions.setViewOption(HubViewOptionKeys.fullScreen, !isFullScreen);
    }

    private _resolveWorkItemsTabFilter = (filter: WorkItemsTabFilter): void  => { 
        this._workItemsTabFilter = filter;

        // When filter is hidden, set it to be focused if it is re-activated
        if (!filter) {
            this._focusFilterBarOnMount = true;
        }

        else {
            if (this._focusFilterBarOnMount) {
                this._focusFilterBarOnMount = false;
                this._workItemsTabFilter.focusFilterBar();
            }
        }
    }
}

